/**
 * Ajustes (`/ajustes`, admin) — edita as três chaves de `GET/PUT /settings`,
 * cada uma em seu próprio painel com seu próprio botão de salvar: empresa,
 * dados bancários e padrões da proposta.
 *
 * Consequência importante, explicada logo no topo: estes valores são
 * copiados para dentro de cada proposta no momento em que ela é emitida
 * (é assim que uma proposta antiga continua íntegra mesmo que a empresa
 * troque de banco depois) — mudar algo aqui não altera nada já emitido,
 * só o que for gerado daqui para frente.
 */
import { useState, type FormEvent } from 'react';
import { useToast } from '@/hooks/useToast';
import {
  useSettings,
  useUpdateBankSettings,
  useUpdateCompanySettings,
  useUpdateProposalDefaults,
} from '@/hooks/useAdmin';
import { ApiError } from '@/lib/api';
import type { BankSettings, CompanySettings, PaymentCondition, ProposalDefaultsSettings, TipoCalculo } from '@/lib/types';
import { maskCNPJ } from '@/lib/format';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import './Admin.css';

const TIPO_CALCULO_HINT: Record<TipoCalculo, string> = {
  dentro: 'Base majorada pela alíquota interna do destino — método mais usado.',
  fora: 'DIFAL calculado direto sobre a diferença de alíquotas.',
};

function swap<T>(list: T[], i: number, j: number): T[] {
  const copy = list.slice();
  const tmp = copy[i];
  copy[i] = copy[j];
  copy[j] = tmp;
  return copy;
}

export function Ajustes() {
  const settingsQuery = useSettings();

  if (settingsQuery.isError && settingsQuery.error instanceof ApiError && settingsQuery.error.status === 403) {
    return (
      <>
        <PageHeader title="Ajustes" />
        <div className="restricted stack">
          <p className="empty-title display">Área restrita</p>
          <p className="muted">Esta seção é só para administradores.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Ajustes"
        meta="Estes valores são copiados para dentro de cada proposta no momento em que ela é emitida — mudar algo aqui não altera propostas já emitidas, só as novas a partir de agora."
      />

      {settingsQuery.isLoading ? (
        <Panel>
          <Spinner label="Carregando ajustes…" />
        </Panel>
      ) : !settingsQuery.data ? (
        <Panel>
          <p className="muted">Não foi possível carregar os ajustes agora. Tente atualizar a página.</p>
        </Panel>
      ) : (
        <div className="stack-lg">
          <EmpresaPanel initial={settingsQuery.data.company} />
          <BancoPanel initial={settingsQuery.data.bank} />
          <PadroesPanel initial={settingsQuery.data.proposalDefaults} />
        </div>
      )}
    </>
  );
}

// -------------------------------------------------------------- empresa --

function EmpresaPanel({ initial }: { initial: CompanySettings }) {
  const toast = useToast();
  const mutation = useUpdateCompanySettings();
  const [form, setForm] = useState<CompanySettings>(initial);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSaving(true);
    try {
      await mutation.mutateAsync(form);
      toast.success('Dados da empresa atualizados.');
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível salvar agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel title="Empresa">
      <form className="stack" onSubmit={handleSubmit} noValidate>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="field-grid">
          <Field
            label="Razão social"
            required
            value={form.razaoSocial}
            onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })}
            error={fieldErrors.razaoSocial}
          />
          <Field
            label="CNPJ"
            required
            value={form.cnpj}
            onChange={(e) => setForm({ ...form, cnpj: maskCNPJ(e.target.value) })}
            error={fieldErrors.cnpj}
          />
        </div>
        <Field
          as="textarea"
          label="Endereço"
          required
          value={form.endereco}
          onChange={(e) => setForm({ ...form, endereco: e.target.value })}
          error={fieldErrors.endereco}
        />
        <Field
          label="NCM"
          required
          hint="Código fiscal usado no documento da proposta."
          value={form.ncm}
          onChange={(e) => setForm({ ...form, ncm: e.target.value })}
          error={fieldErrors.ncm}
        />
        <div className="row row-end">
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? 'Salvando…' : 'Salvar dados da empresa'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

// --------------------------------------------------------- dados bancários --

function BancoPanel({ initial }: { initial: BankSettings }) {
  const toast = useToast();
  const mutation = useUpdateBankSettings();
  const [form, setForm] = useState<BankSettings>(initial);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSaving(true);
    try {
      await mutation.mutateAsync(form);
      toast.success('Dados bancários atualizados.');
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível salvar agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel title="Dados bancários">
      <form className="stack" onSubmit={handleSubmit} noValidate>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="field-grid">
          <Field
            label="Banco"
            required
            value={form.banco}
            onChange={(e) => setForm({ ...form, banco: e.target.value })}
            error={fieldErrors.banco}
          />
          <Field
            label="Agência"
            required
            value={form.agencia}
            onChange={(e) => setForm({ ...form, agencia: e.target.value })}
            error={fieldErrors.agencia}
          />
        </div>
        <div className="field-grid">
          <Field
            label="Conta"
            required
            value={form.conta}
            onChange={(e) => setForm({ ...form, conta: e.target.value })}
            error={fieldErrors.conta}
          />
          <Field
            label="Chave pix"
            required
            value={form.pix}
            onChange={(e) => setForm({ ...form, pix: e.target.value })}
            error={fieldErrors.pix}
          />
        </div>
        <div className="row row-end">
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? 'Salvando…' : 'Salvar dados bancários'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

// ------------------------------------------------------- padrões da proposta --

function PadroesPanel({ initial }: { initial: ProposalDefaultsSettings }) {
  const toast = useToast();
  const mutation = useUpdateProposalDefaults();
  const [form, setForm] = useState<ProposalDefaultsSettings>(initial);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  function updateCondicao(index: number, patch: Partial<PaymentCondition>) {
    setForm((prev) => ({
      ...prev,
      condicoesPagamento: prev.condicoesPagamento.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));
  }

  function addCondicao() {
    setForm((prev) => ({
      ...prev,
      condicoesPagamento: [...prev.condicoesPagamento, { titulo: '', descricao: '' }],
    }));
  }

  function removeCondicao(index: number) {
    setForm((prev) => ({
      ...prev,
      condicoesPagamento: prev.condicoesPagamento.filter((_, i) => i !== index),
    }));
  }

  function moveCondicao(index: number, direction: -1 | 1) {
    setForm((prev) => ({
      ...prev,
      condicoesPagamento: swap(prev.condicoesPagamento, index, index + direction),
    }));
  }

  function updateObservacao(index: number, value: string) {
    setForm((prev) => ({
      ...prev,
      observacoes: prev.observacoes.map((o, i) => (i === index ? value : o)),
    }));
  }

  function addObservacao() {
    setForm((prev) => ({ ...prev, observacoes: [...prev.observacoes, ''] }));
  }

  function removeObservacao(index: number) {
    setForm((prev) => ({ ...prev, observacoes: prev.observacoes.filter((_, i) => i !== index) }));
  }

  function moveObservacao(index: number, direction: -1 | 1) {
    setForm((prev) => ({ ...prev, observacoes: swap(prev.observacoes, index, index + direction) }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSaving(true);
    try {
      await mutation.mutateAsync({
        ...form,
        validadeDias: Number(form.validadeDias) || 0,
        observacoes: form.observacoes.filter((o) => o.trim().length > 0),
      });
      toast.success('Padrões da proposta atualizados.');
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setFieldErrors(err.fields ?? {});
      } else {
        setFormError('Não foi possível salvar agora. Tente de novo.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel title="Padrões da proposta">
      <form className="stack" onSubmit={handleSubmit} noValidate>
        {formError ? (
          <p className="form-alert form-alert-error" role="alert">
            {formError}
          </p>
        ) : null}

        <div className="field-grid">
          <Field
            label="Validade da proposta (dias)"
            type="number"
            min={1}
            required
            value={form.validadeDias}
            onChange={(e) => setForm({ ...form, validadeDias: Number(e.target.value) })}
            error={fieldErrors.validadeDias}
          />
          <Field
            as="select"
            label="Tipo de cálculo padrão"
            hint={TIPO_CALCULO_HINT[form.tipoCalculo]}
            value={form.tipoCalculo}
            onChange={(e) => setForm({ ...form, tipoCalculo: e.target.value as TipoCalculo })}
          >
            <option value="dentro">Por dentro</option>
            <option value="fora">Por fora</option>
          </Field>
        </div>

        <div className="stack-sm">
          <p className="field-label">Condições de pagamento</p>
          {fieldErrors.condicoesPagamento ? <p className="field-error">{fieldErrors.condicoesPagamento}</p> : null}
          <div className="repeat-list">
            {form.condicoesPagamento.map((condicao, index) => (
              <div className="repeat-row" key={index}>
                <span className="repeat-row__index">{index + 1}</span>
                <div className="repeat-row__fields">
                  <Field
                    label="Título"
                    required
                    value={condicao.titulo}
                    onChange={(e) => updateCondicao(index, { titulo: e.target.value })}
                  />
                  <Field
                    label="Descrição"
                    required
                    value={condicao.descricao}
                    onChange={(e) => updateCondicao(index, { descricao: e.target.value })}
                  />
                </div>
                <div className="repeat-row__actions">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={index === 0}
                    onClick={() => moveCondicao(index, -1)}
                  >
                    Subir
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={index === form.condicoesPagamento.length - 1}
                    onClick={() => moveCondicao(index, 1)}
                  >
                    Descer
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={form.condicoesPagamento.length === 1}
                    onClick={() => removeCondicao(index)}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div>
            <Button type="button" size="sm" variant="ghost" onClick={addCondicao}>
              Adicionar condição
            </Button>
          </div>
        </div>

        <div className="stack-sm">
          <p className="field-label">Observações</p>
          <p className="field-hint">Linhas curtas que aparecem no rodapé do documento da proposta.</p>
          {form.observacoes.length === 0 ? (
            <p className="muted">Nenhuma observação padrão.</p>
          ) : (
            <div className="repeat-list">
              {form.observacoes.map((observacao, index) => (
                <div className="repeat-row" key={index}>
                  <span className="repeat-row__index">{index + 1}</span>
                  <div className="repeat-row__fields">
                    <Field
                      label={`Observação ${index + 1}`}
                      value={observacao}
                      onChange={(e) => updateObservacao(index, e.target.value)}
                    />
                  </div>
                  <div className="repeat-row__actions">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={index === 0}
                      onClick={() => moveObservacao(index, -1)}
                    >
                      Subir
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={index === form.observacoes.length - 1}
                      onClick={() => moveObservacao(index, 1)}
                    >
                      Descer
                    </Button>
                    <Button type="button" size="sm" variant="danger" onClick={() => removeObservacao(index)}>
                      Remover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div>
            <Button type="button" size="sm" variant="ghost" onClick={addObservacao}>
              Adicionar observação
            </Button>
          </div>
        </div>

        <Field
          as="textarea"
          label="Aviso legal"
          required
          hint="Texto fixo impresso no rodapé de toda proposta."
          value={form.disclaimer}
          onChange={(e) => setForm({ ...form, disclaimer: e.target.value })}
          error={fieldErrors.disclaimer}
        />

        <div className="row row-end">
          <Button type="submit" disabled={saving || !dirty}>
            {saving ? 'Salvando…' : 'Salvar padrões da proposta'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
